import { isAxiosError } from "axios";
import { env } from "../constants/env";
import { describeFieldErrors } from "./fieldLabels";

// Mapping des codes d'erreur métier renvoyés par le backend vers des messages
// FR affichables tels quels dans l'UI (toast / alert).
// Source : docs-technique.html → section "Erreurs Wallet / Order".

const ERROR_MESSAGES: Record<string, string> = {
  // Wallet
  INSUFFICIENT_FUNDS:        "Solde du wallet insuffisant.",
  // Alias : openapi v2.1.0 illustre le schéma `Error` avec INSUFFICIENT_BALANCE
  // alors que le 422 de /wallet/block documente INSUFFICIENT_FUNDS. Le backend
  // peut renvoyer l'un ou l'autre — on mappe les deux sur le même message.
  INSUFFICIENT_BALANCE:      "Solde du wallet insuffisant.",
  // 3e alias — c'est le nom RÉELLEMENT documenté côté backend (doc d'intégration
  // du 2026-08-01, code 422). Sans lui, un solde insuffisant retombait sur le
  // fallback HTTP 422 et affichait « Données invalides. » — incompréhensible.
  // Le montant exact manquant est ajouté par `getErrorMessage` (cf. plus bas).
  INSUFFICIENT_WALLET_BALANCE: "Solde du wallet insuffisant.",
  // Provision impossible côté serveur : l'action est rejouable telle quelle.
  WALLET_HOLD_FAILED:        "La réservation des fonds a échoué. Réessaie.",
  // 422 sur /payments/initiate (note backend du 2026-08-05). Ne devrait pas
  // survenir si l'écran appelle /payments/estimate d'abord — sauf sur un écran
  // resté ouvert pendant que le seuil changeait côté serveur. Le message exact
  // (« minimum 2000 XOF ») vient du backend : il est configurable, donc jamais
  // de montant codé en dur ici.
  AMOUNT_BELOW_MINIMUM:      "Le montant est inférieur au minimum autorisé.",
  WALLET_SUSPENDED:          "Ton wallet est suspendu. Contacte le support.",
  HOLD_NOT_FOUND:            "La réservation de fonds est introuvable.",
  HOLD_EXPIRED:              "La réservation de fonds a expiré.",
  DUPLICATE_IDEMPOTENCY_KEY: "Cette opération a déjà été effectuée.",
  // Orders
  INVALID_TRANSITION:        "Cette action n'est pas autorisée à ce stade.",
  ORDER_ALREADY_CANCELLED:   "Commande déjà annulée.",
  ORDER_NOT_CANCELLABLE:     "La commande est trop avancée pour être annulée.",
  // Matching
  NO_AGENTS_AVAILABLE:       "Aucune Main disponible dans ta zone.",
  AGENT_NOT_CLAIMABLE:       "Cette Main n'est plus disponible.",
  // 410 — un DÉLAI est passé (offre envoyée à un autre agent, validation client
  // expirée). L'écran affiché est périmé : il doit être rechargé, pas réessayé.
  GONE:                      "Ce délai est dépassé. Actualise pour voir l'état à jour.",
  // 409 générique — message NEUTRE : `CONFLICT` remonte aussi bien d'un blocage
  // de fonds que d'une transition d'arrêt invalide ou d'une double annulation.
  // L'ancien libellé (« Impossible de bloquer les fonds ») était faux partout
  // ailleurs que sur la création de course. Le vrai motif vient du `message`
  // backend, que `getErrorMessage` privilégie déjà quand il est présent.
  CONFLICT:                  "Cette action n'est plus possible dans l'état actuel.",
  // Generic — le backend documente le code en MAJUSCULES (schéma ValidationError
  // de l'openapi) mais a historiquement renvoyé la forme minuscule.
  VALIDATION_ERROR:          "Certains champs sont invalides.",
  validation_error:          "Certains champs sont invalides.",
};

const HTTP_FALLBACK: Record<number, string> = {
  400: "Requête invalide.",
  401: "Session expirée. Reconnecte-toi.",
  // 402 : introduit par openapi v2.1.0 sur POST /payments/initiate — solde wallet
  // insuffisant pour couvrir le montant + les frais d'un RETRAIT.
  402: "Solde insuffisant pour couvrir ce retrait et ses frais.",
  403: "Action non autorisée.",
  404: "Ressource introuvable.",
  409: "Conflit avec une autre opération.",
  // 410 — délai expiré (offre, validation client). L'état local est périmé.
  410: "Ce délai est dépassé. Actualise pour voir l'état à jour.",
  413: "Fichier trop volumineux.",
  // §7 du contrat du 2026-08-31 : nommer les formats acceptés plutôt que de
  // constater le refus. « Format non supporté » laisse la personne réessayer
  // avec le même fichier — la liste, elle, lui dit quoi faire.
  415: "Format non accepté. Utilise JPEG, PNG, WebP ou MP4.",
  // 422 — porte AUSSI INSUFFICIENT_WALLET_BALANCE, mais ce cas est capté plus haut
  // par son code métier : ce libellé ne sert qu'aux vraies erreurs de validation.
  422: "Données invalides.",
  429: "Trop de requêtes. Réessaie dans une minute.",
  500: "Erreur serveur. Réessaie plus tard.",
  502: "Le service est indisponible.",
  503: "Le service est temporairement indisponible.",
  504: "Le serveur met trop de temps à répondre.",
};

// Forme du body d'erreur backend (openapi v2).
//   { success: false, message, error: { code, details?, fields? }, metadata }
// Pour la rétrocompat, on accepte aussi l'ancienne forme plate `{ error, details }`
// au cas où un endpoint pas encore migré serait rencontré.
export type ApiErrorShape = {
  success?: boolean;
  message?: string;
  error?:
    | string
    | {
        code?:    string;
        details?: unknown;
        fields?:  { field?: string; message?: string }[];
      };
  details?: unknown;
};

export type NormalizedError = {
  status:  number;          // 0 = pas de réponse HTTP (réseau, abort…)
  code:    string;          // code métier ou "network"/"unknown"
  message: string;          // libellé FR affichable
  details: unknown;
  fields?: { field?: string; message?: string }[];
  /**
   * `metadata.request_id` de la réponse — l'identifiant que le serveur donne à
   * CETTE requête.
   *
   * Le document des formats du 2026-09-04 demande de le journaliser : « c'est
   * ce qui permet de retrouver une requête précise dans les journaux du
   * serveur. Sans lui, un incident se cherche à l'aveugle. »
   *
   * ⚠️ À journaliser, JAMAIS à afficher — pas plus qu'`error.code`. C'est une
   * référence pour nous et pour le support, pas une information utilisateur.
   */
  requestId?: string;
  raw:     unknown;         // erreur originale (debug)
};

/**
 * Ne retourne une valeur QUE si c'est une chaîne réellement affichable.
 *
 * Le contrat annonce `message: string`, mais un backend qui renvoie un objet
 * (dict de validation, erreur sérialisée…) produisait un `[object Object]` à
 * l'écran — vu en production sur l'écran de confirmation de RETRAIT, où
 * l'utilisateur ne comprenait donc pas pourquoi son virement échouait.
 * TypeScript ne protège pas des données runtime : on valide ici.
 */
function asDisplayString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  return undefined;
}

/**
 * ⚠️ `GENERIC_CODES` a été RETIRÉ le 2026-09-05.
 *
 * Il servait à décider quels de nos libellés cédaient le pas à la liste des
 * champs en faute — une arbitration qui n'a plus lieu d'être depuis que le
 * message du backend passe devant tout le reste (cf. `normalizeError`).
 *
 * La précédence tient désormais en une phrase : le message du serveur, puis
 * les champs en faute d'un 422, puis nos libellés, puis le repli HTTP.
 */

function extractErrorParts(data: ApiErrorShape): {
  code:    string | undefined;
  details: unknown;
  fields:  { field?: string; message?: string }[] | undefined;
} {
  if (typeof data.error === "object" && data.error !== null) {
    return {
      code:    data.error.code,
      details: data.error.details,
      fields:  data.error.fields,
    };
  }
  // Ancienne forme : `error: "CODE_STRING"` au top level
  return {
    code:    typeof data.error === "string" ? data.error : undefined,
    details: data.details,
    fields:  undefined,
  };
}

/**
 * Normalise n'importe quelle erreur en `NormalizedError`. Utilisé dans les
 * `onError` de mutations et dans le wrapper toast.
 */
export function normalizeError(err: unknown): NormalizedError {
  if (isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const data   = (err.response?.data ?? {}) as ApiErrorShape;
    const { code: errCode, details, fields } = extractErrorParts(data);
    const requestId = asDisplayString(
      (data as { metadata?: { request_id?: unknown } }).metadata?.request_id,
    );
    const code   = errCode ?? (status === 0 ? "network" : `http_${status}`);

    // Alerte DEV : un `message` non-string est une violation du contrat backend
    // (openapi : `message: string`). Sans ce garde-fou il s'affichait tel quel.
    if (env.isDev && data.message !== undefined && asDisplayString(data.message) === undefined) {
      console.warn(
        "[api/errors] `message` non affichable reçu du backend (contrat: string) :",
        data.message,
      );
    }

    // ⚠️ LE MESSAGE DU BACKEND PASSE EN PREMIER — inversion du 2026-09-05.
    //
    // Jusqu'ici nos libellés FR passaient devant : le `message` serveur était
    // souvent technique, parfois anglais, et un libellé maîtrisé valait mieux.
    // Le backend renseigne désormais un message destiné à l'utilisateur sur
    // TOUS les endpoints, et la hiérarchie s'inverse pour trois raisons :
    //
    //   • il sait POURQUOI il a refusé. Nos libellés sont indexés sur un code,
    //     qui recouvre souvent plusieurs situations — `CONFLICT` remonte aussi
    //     bien d'un blocage de fonds que d'une transition invalide ;
    //   • son libellé suit les évolutions de la règle SANS qu'on redéploie ;
    //   • un message précis vaut mieux qu'un message maîtrisé mais vague : le
    //     client préfère « Une main est déjà engagée sur cette course » à
    //     « Cette action n'est plus possible dans l'état actuel ».
    //
    // Nos tables deviennent le REPLI, pour les réponses qui n'en portent pas.
    //
    // La liste FR des champs en faute d'un 422 reste juste derrière : elle nomme
    // les champs un par un, ce qu'aucune phrase de synthèse ne fait, et elle
    // remplace les messages de validation ANGLAIS de Laravel, que le contrat
    // interdit d'afficher.
    const mapped = errCode ? ERROR_MESSAGES[errCode] : undefined;

    const message =
      asDisplayString(data.message) ||
      describeFieldErrors(fields) ||
      mapped ||
      HTTP_FALLBACK[status] ||
      (status === 0 ? "Pas de connexion. Vérifie ton réseau." : "Une erreur est survenue.");

    return { status, code, message, details, fields, requestId, raw: err };
  }

  if (err instanceof Error) {
    return {
      status: 0,
      code: "unknown",
      message: asDisplayString(err.message) ?? "Erreur inattendue.",
      details: undefined,
      raw: err,
    };
  }

  return { status: 0, code: "unknown", message: "Erreur inattendue.", details: undefined, raw: err };
}

/**
 * Retourne uniquement le message à afficher — raccourci pour l'UI.
 *
 * Dernier filet : quoi qu'il arrive en amont, l'UI reçoit une CHAÎNE. Un objet
 * qui remonterait jusqu'ici s'afficherait « [object Object] » — inacceptable,
 * a fortiori sur un écran de paiement.
 */
export function getErrorMessage(err: unknown): string {
  return asDisplayString(normalizeError(err).message) ?? "Une erreur est survenue.";
}

/**
 * Message que le BACKEND a rédigé pour l'utilisateur, sans notre relecture.
 *
 * `getErrorMessage` privilégie délibérément nos libellés maîtrisés : un
 * `message` serveur est souvent technique, parfois anglais. Mais le contrat du
 * 2026-08-26 nomme un cas où c'est l'inverse — l'annulation client refusée
 * après affectation :
 *
 *   « Le 409 porte un message destiné à l'utilisateur : "Une main est déjà
 *     engagée sur cette course : passez par le support pour l'annuler."
 *     Affichez-le tel quel plutôt que d'écrire le vôtre. »
 *
 * Le backend sait pourquoi il a refusé, et son libellé suivra les évolutions de
 * la règle sans qu'on redéploie l'app. Cet accesseur existe pour ces cas-là,
 * NOMMÉMENT — pas pour relayer n'importe quelle erreur serveur à l'écran.
 *
 * Rend `undefined` si la réponse n'en porte pas : l'appelant fournit alors son
 * propre repli.
 */
export function backendUserMessage(err: unknown): string | undefined {
  if (!isAxiosError(err)) return undefined;
  const data = (err.response?.data ?? {}) as ApiErrorShape;
  return asDisplayString(data.message);
}

/**
 * Montant qui MANQUE au wallet, extrait d'une erreur de solde insuffisant.
 *
 * Le backend renvoie le chiffre exact dans `error.details.missing_amount`
 * (code `INSUFFICIENT_WALLET_BALANCE`, HTTP 422) :
 *
 *     { code: "INSUFFICIENT_WALLET_BALANCE",
 *       details: { hold_amount: 1440, available_balance: 1000, missing_amount: 440 } }
 *
 * L'afficher évite le message générique « solde insuffisant » : le client sait
 * combien recharger, au franc près.
 *
 * Retourne `null` dès que la valeur n'est pas un nombre fini strictement
 * positif — un 0 ou un NaN affiché comme « il te manque 0 FCFA » serait pire
 * que pas de montant du tout.
 */
export function missingWalletAmount(err: unknown): number | null {
  const { details } = normalizeError(err);
  if (typeof details !== "object" || details === null) return null;

  const raw = (details as { missing_amount?: unknown }).missing_amount;
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return null;
  return raw;
}
